import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@apollo/client';
import axios from 'axios';
import { Buffer } from 'buffer';
import { v4 as uuid4 } from 'uuid';

import {
  CREATE_MEDIA_UPLOAD,
  COMPLETE_MEDIA_UPLOAD,
} from '../../../../http/apollo-queries';
import { Attachment } from '../types';

const uploadImageToS3Url = (imageFile: string, url: string) => {
  const bufferdata = Buffer.from(imageFile, 'base64');

  return axios({
    method: 'put',
    url,
    data: bufferdata,
    headers: { 'Content-Type': 'image/png', 'Content-Encoding': 'base64' },
  });
};

interface PropTypes {
  setSaveAttachmentFn: React.Dispatch<
    React.SetStateAction<(() => Promise<string | undefined>) | null>
  >;
}

const ImageDropZone: React.FC<PropTypes> = ({ setSaveAttachmentFn }) => {
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [createMediaUpload] = useMutation(CREATE_MEDIA_UPLOAD);
  const [completeMediaUpload] = useMutation(COMPLETE_MEDIA_UPLOAD);

  const onDropAccepted = useCallback((acceptedFiles: any) => {
    createMediaUpload({
      variables: {
        filename: acceptedFiles[0].name,
        mediaType: 'CHAT',
        refId: `${uuid4()}_${acceptedFiles[0].name}`,
      },
    }).then((res) =>
      setAttachment({
        preview: URL.createObjectURL(acceptedFiles[0]),
        name: acceptedFiles[0].name,
        uploadUrl: res.data.createMediaUpload.uploadUrl,
        key: res.data.createMediaUpload.key,
        mediaId: res.data.createMediaUpload.id,
      })
    );
  }, []);

  const saveAttachment = useCallback(async () => {
    if (attachment) {
      const { uploadUrl, preview } = attachment;

      const imageData = await axios
        .get(preview, {
          responseType: 'arraybuffer',
        })
        .then((response) => response.data);

      const s3UploadResult = await uploadImageToS3Url(imageData, uploadUrl);

      if (s3UploadResult.status === 200) {
        const { data } = await completeMediaUpload({
          mutation: COMPLETE_MEDIA_UPLOAD,
          variables: {
            mediaId: attachment.mediaId,
          },
        });

        if (data?.completeMediaUpload) {
          return data.completeMediaUpload.size.avatarLarge;
        }
      }
    }
  }, [attachment]);

  useEffect(() => {
    setSaveAttachmentFn(attachment ? () => saveAttachment : null);
  }, [saveAttachment, attachment]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    multiple: false,
  });

  return (
    <div {...getRootProps()} className="h-full flex flex-col">
      <input {...getInputProps()} />
      <div>
        {isDragActive ? (
          <p>Drop the files here ...</p>
        ) : (
          <p>Drag 'n' drop some files here, or click to select files</p>
        )}
      </div>
      {attachment ? (
        <div className="relative h-full">
          <img
            alt={'attachment_preview'}
            src={attachment.preview}
            className="max-h-1/2"
          />
          <span
            className="material-icons text-2xl absolute top-0 right-0"
            onClick={(e) => {
              e.stopPropagation();
              setAttachment(null);
            }}
          >
            close
          </span>
        </div>
      ) : (
        <div className="text-center">
          <span className="material-icons text-5xl">image</span>
        </div>
      )}
    </div>
  );
};

export default ImageDropZone;
